import { useState } from 'react'
import { Send, CheckCircle } from 'lucide-react'
import { BOOKING_TYPES } from '../data'
import { bookings as bookingsApi, ApiError } from '../lib/api'

interface FormState {
  name: string
  email: string
  phone: string
  eventType: string
  eventDate: string
  venue: string
  city: string
  message: string
}

const INITIAL_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  eventType: '',
  eventDate: '',
  venue: '',
  city: '',
  message: '',
}

export default function Contact() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      await bookingsApi.submit({
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        eventType: form.eventType as Parameters<typeof bookingsApi.submit>[0]['eventType'],
        eventDate: form.eventDate || undefined,
        venue: form.venue || undefined,
        city: form.city || undefined,
        message: form.message,
      })
      setSubmitted(true)
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Submission failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'var(--ink-2)',
    border: '1px solid var(--border)',
    borderRadius: 0,
    color: 'var(--cream)',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    padding: '12px 14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    appearance: 'none',
  }

  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: '10px',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: 'var(--gold-dim)',
    display: 'block',
    marginBottom: '8px',
  }

  if (submitted) {
    return (
      <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 32px' }}>
        <div style={{ textAlign: 'center', maxWidth: '480px' }}>
          <CheckCircle size={48} color="var(--gold)" style={{ margin: '0 auto 24px' }} />
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: '40px',
              textTransform: 'uppercase',
              color: 'var(--cream)',
              marginBottom: '16px',
            }}
          >
            Request Received
          </h1>
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '32px' }}>
            Thanks for reaching out! We'll review your booking request and get back to you within 24–48 hours.
          </p>
          <button className="btn-ghost" onClick={() => setSubmitted(false)}>
            Send Another Request
          </button>
        </div>
      </main>
    )
  }

  return (
    <main style={{ minHeight: '100vh', padding: 'clamp(40px, 8vw, 80px) clamp(16px, 5vw, 32px)' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '56px', paddingBottom: '24px', borderBottom: '1px solid var(--border)' }}>
          <div className="section-label">Bookings & Inquiries</div>
          <h1 className="section-title">Contact</h1>
          <p
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: '15px',
              color: 'var(--muted)',
              marginTop: '16px',
              maxWidth: '500px',
            }}
          >
            For bookings, press, and general inquiries — fill out the form below and we'll be in touch.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {/* Row: Name + Email */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle} htmlFor="name">Your Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={handleChange}
                placeholder="Full name"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="email">Email Address *</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
          </div>

          {/* Row: Phone + Event Type */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle} htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="(555) 000-0000"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="eventType">Event Type *</label>
              <select
                id="eventType"
                name="eventType"
                required
                value={form.eventType}
                onChange={handleChange}
                style={{ ...inputStyle, color: form.eventType ? 'var(--cream)' : 'var(--muted)', cursor: 'pointer' }}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              >
                <option value="" disabled style={{ background: 'var(--ink-2)', color: 'var(--muted)' }}>
                  Select type...
                </option>
                {BOOKING_TYPES.map((t) => (
                  <option key={t} value={t} style={{ background: 'var(--ink-2)', color: 'var(--cream)' }}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row: Date + Venue */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle} htmlFor="eventDate">Event Date</label>
              <input
                id="eventDate"
                name="eventDate"
                type="date"
                value={form.eventDate}
                onChange={handleChange}
                style={{ ...inputStyle, colorScheme: 'dark' }}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="city">City / Location</label>
              <input
                id="city"
                name="city"
                type="text"
                value={form.city}
                onChange={handleChange}
                placeholder="Nashville, TN"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
                onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
          </div>

          {/* Venue */}
          <div>
            <label style={labelStyle} htmlFor="venue">Venue Name</label>
            <input
              id="venue"
              name="venue"
              type="text"
              value={form.venue}
              onChange={handleChange}
              placeholder="Venue or location name"
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
              onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Message */}
          <div>
            <label style={labelStyle} htmlFor="message">Tell Us About Your Event *</label>
            <textarea
              id="message"
              name="message"
              required
              value={form.message}
              onChange={handleChange}
              placeholder="Expected attendance, set duration, any special requests..."
              rows={6}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              onFocus={(e) => (e.target.style.borderColor = 'var(--gold-dim)')}
              onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'wait' : 'pointer',
              }}
            >
              <Send size={14} />
              {loading ? 'Sending...' : 'Send Booking Request'}
            </button>
          </div>
        </form>
      </div>
    </main>
  )
}
